import React from "react";
import { queryNoTracking } from "../../services/DataService";
import { t } from "../../services/HelperService";
import { RTLGridCtrl } from "../grids/RTLGridCtrl";
import { Helper } from "../../services/JobService";
import { SummaryCellTemplate } from "../grids/GridColumnTemplates";

const qSuggested = queryNoTracking("Search_Job_ForContact"); // (contactID, search)
const qAll = queryNoTracking("Search_Job");            // (search)

const COLUMNS = [
    { fieldName: "Reference", caption: "Reference", width: 140 },
    { fieldName: "Contact", caption: "Contact", width: 200 },
    { fieldName: "Company", caption: "Company", width: 200 },
    { fieldName: "Status", caption: "Status", width: 130 },
    { fieldName: "Stages", caption: "Stage", width: 180 },
    { fieldName: "ETA", caption: "ETA", width: 120, format: "{0:dd/MM/yyyy}" },
];

export default function ChatJobSelector({
    contactID = null,
    // single-select (default)
    value = null,                 // number | null (JobID)
    onChange,                     // (row|null) => void

    // multi-select (for LinkJobsDlg)
    multiple = false,
    selectedIds = [],             // number[]
    onChangeSelected,             // (rows) => void
    methodsRef = null,
    height = 340,
    gridId = "job_select",
    defaultTab = "suggested",
}) {
    const gridRef = React.useRef(null);
    const [tab, setTab] = React.useState(
        contactID ? defaultTab : "all"
    );

    React.useEffect(() => {
        if (!contactID && tab === "suggested") setTab("all");
    }, [contactID, tab]);

    // Track the last contact we auto-switched for
    const lastAutoFor = React.useRef(null);
    
    React.useEffect(() => {
        // No contact -> force back to "all" and reset tracker
        if (!contactID) {
            if (tab === "suggested") setTab("all");
            lastAutoFor.current = null;
            return;
        }
        // Contact changed -> auto-switch to "suggested" once
        if (lastAutoFor.current !== contactID) {
            lastAutoFor.current = contactID;
            if (tab !== "suggested") setTab("suggested");
        }
        // If user manually switches tabs after this, we won't fight them.
    }, [contactID, tab]);

    // preselect on load
    React.useEffect(() => {
        let cancelled = false;
        const tick = () => {
            if (cancelled || !gridRef.current) return;
            const data = gridRef.current.getData?.() || [];
            if (!data.length) { setTimeout(tick, 60); return; }

            if (multiple && selectedIds?.length) {
                gridRef.current.select(selectedIds);
            } else if (!multiple && value) {
                gridRef.current.select([value]);
            }
        };
        tick();
        return () => { cancelled = true; };
    }, [tab, multiple, selectedIds, value]);

    React.useEffect(() => {
        if (!methodsRef) return;
        methodsRef.current = {
            getData: () => gridRef.current?.getData?.() || [],
            select: (ids) => gridRef.current?.select?.(ids ?? []),
            getSelectedRows: () => gridRef.current?.getSelectedRows?.() || gridRef.current?.getSelected?.() || [],
        };
        return () => { methodsRef.current = null; };
    }, [methodsRef, tab]);

    const getQueryable = React.useCallback(
        (s) => {
            const term = (s ?? "").trim();
            return (tab === "suggested" && contactID)
                ? qSuggested.getAllAsQueryable({ contactID, search: term })
                : qAll.getAllAsQueryable({ search: term });
        },
        [tab, contactID]
    );

    const handleRowClick = React.useCallback(({ row }) => {
        if (!row) return;
        if (multiple) {
            // toggle-like behavior via grid API
            const next = gridRef.current.toggleSelect?.(row.ID)
                ?? gridRef.current.select?.([row.ID], { append: true });
            const rows = gridRef.current.getSelectedRows?.() || [];
            onChangeSelected?.(rows);
        } else {
            gridRef.current.select([row.ID]);
            onChange?.(row);
        }
    }, [multiple, onChange, onChangeSelected]);

    const processColumn = React.useCallback((col) => {
        if (col.field === "Stages") {
            col.cellTemplate = (p) => (
                <SummaryCellTemplate
                    value={p.value}
                    translate
                    color={col.field === "Stages" ? Helper.getJobLineStageColor(p.dataItem.Stage) : undefined}
                />
            );
            col.filterable = false;
        }

        if (col.field === "Status") {
            // Status likely needs translation too
            col.requireTranslate = true;
        }

        return col;
    }, []);

    // Re-apply external selection whenever tab changes or selectedIds update (multi-select)
    React.useEffect(() => {
        if (!multiple) return;
        let cancelled = false;
        const apply = () => {
            if (cancelled) return;
            const data = gridRef.current?.getData?.() || [];
            // defer until rows for the tab are present
            if (!data.length) { setTimeout(apply, 60); return; }
            if (Array.isArray(selectedIds)) {
                gridRef.current?.select?.(selectedIds);
            }
        };
        apply();
        return () => { cancelled = true; };
    }, [tab, multiple, selectedIds]);

    return (
        <div>
            <div className="btn-group mb-2">
                <button
                    type="button"
                    className={`btn btn-sm ${tab === "suggested" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setTab("suggested")}
                    disabled={!contactID}
                    title={!contactID ? t("application_strings.views.chat.jobSelector.suggestedTitle") : ""}
                >
                    {t("application_strings.views.chat.jobSelector.suggested")}
                </button>
                <button
                    type="button"
                    className={`btn btn-sm ${tab === "all" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setTab("all")}
                >
                    {t("application_strings.views.chat.jobSelector.searchAll")}
                </button>
            </div>

            <RTLGridCtrl
                methods={gridRef}
                gridID={`${gridId}_${tab}_${multiple ? "multi" : "single"}`}
                strings="activeJobTrackingView"
                query={getQueryable}
                columns={COLUMNS}
                allowSearch={false}
                showToolbar={false}
                showDetail={false}
                showRowActions={false}
                actionCell={null}
                showCheckbox={multiple}
                rowKeyField="ID"
                height={height}
                onRowClick={handleRowClick}
                autoSelectFirst={false}
                processColumn={processColumn}
            />
        </div>
    );
}
